import { RestClient, RestError } from "./restClient";

export interface PublisherSelf {
  slug: string;
  display_name: string;
  verified: boolean;
  suspended: boolean;
}
export interface PendingPublisher {
  slug: string;
  display_name: string;
  contact_email: string;
}
export interface ApplyBody {
  slug: string;
  display_name: string;
  contact_email: string;
  homepage_url?: string;
}
export interface Submission {
  name: string;
  version: string;
  listing_state: string;
  quarantine_reason: string | null;
  reviewed_at: string | null;
  created_at: string;
}

/** Tenant self-serve publisher onboarding + staff verification, backed by
 *  plugin_platform_service's /publishers routes (proxied under
 *  /plugins/v1/...). */
export class PublisherApi {
  constructor(private client: RestClient) {}

  /** GET /plugins/v1/publishers/self — null if the caller's company has no
   *  publisher application yet. */
  getSelf(): Promise<PublisherSelf | null> {
    return this.client.get<PublisherSelf | null>("/plugins/v1/publishers/self");
  }

  /** POST /plugins/v1/publishers/self — apply for a publisher slug. 409 if
   *  the company already has one. */
  apply(body: ApplyBody): Promise<{ slug: string; verified: boolean }> {
    return this.client.post<{ slug: string; verified: boolean }>(
      "/plugins/v1/publishers/self",
      body,
    );
  }

  /** GET /plugins/v1/admin/publishers/pending — staff-only list of
   *  unverified publisher applications. */
  listPending(): Promise<PendingPublisher[]> {
    return this.client
      .get<{ publishers: PendingPublisher[] }>("/plugins/v1/admin/publishers/pending")
      .then((r) => r.publishers);
  }

  /** POST /plugins/v1/publishers/{slug}/verify — staff-only verification. */
  verify(slug: string): Promise<void> {
    return this.client.post<void>(
      `/plugins/v1/publishers/${encodeURIComponent(slug)}/verify`,
      {},
    );
  }

  registerKey(slug: string, publicKeyB64: string): Promise<{ key_fingerprint: string }> {
    return this.client.post<{ key_fingerprint: string }>(
      `/plugins/v1/publishers/${encodeURIComponent(slug)}/keys`,
      { public_key_b64: publicKeyB64 },
    );
  }

  listKeys(slug: string): Promise<string[]> {
    return this.client
      .get<{ slug: string; active_keys: string[] }>(
        `/plugins/v1/publishers/${encodeURIComponent(slug)}/keys`,
      )
      .then((r) => r.active_keys);
  }

  listSubmissions(slug: string): Promise<Submission[]> {
    return this.client
      .get<{ submissions: Submission[] }>(
        `/plugins/v1/publishers/${encodeURIComponent(slug)}/submissions`,
      )
      .then((r) => r.submissions);
  }

  revokeKey(slug: string, fingerprint: string): Promise<void> {
    return this.client.delete<void>(
      `/plugins/v1/publishers/${encodeURIComponent(slug)}/keys/${encodeURIComponent(fingerprint)}`,
    );
  }

  /** POST /plugins/v1/registry/publish — multipart upload of a signed
   *  plugin tarball. Errors come back as FastAPI's
   *  `{"detail": {"error", "message", "details"}}` (the error object is
   *  NESTED under `detail`, not `.message`/`.detail` directly — some
   *  endpoints instead send a plain-string `detail`), so this bypasses
   *  RestClient's throw-on-non-ok `request` path via `rawResponse` and
   *  parses the backend message itself. */
  async publish(
    tarballBytes: Uint8Array,
    signatureB64: string,
  ): Promise<{ status?: string; name?: string; version?: string }> {
    const form = new FormData();
    form.append(
      "tarball",
      new Blob([tarballBytes as BlobPart], { type: "application/gzip" }),
      "plugin.tar.gz",
    );
    // NOTE: do NOT set Content-Type — the browser adds the multipart boundary.
    const resp = await this.client.rawResponse("/plugins/v1/registry/publish", {
      method: "POST",
      headers: { "X-Signature": signatureB64 },
      body: form,
    });
    if (!resp.ok) {
      let message: string = resp.statusText;
      try {
        const body = await resp.json();
        message =
          body?.detail?.message ??
          body?.detail?.error ??
          (typeof body?.detail === "string" ? body.detail : undefined) ??
          body?.message ??
          resp.statusText;
      } catch {
        /* keep statusText */
      }
      throw new RestError(resp.status, message, "/plugins/v1/registry/publish");
    }
    return (await resp.json()) as { status?: string; name?: string; version?: string };
  }
}
